/*!
 * BeforeAfter - a drag-to-reveal before/after image slider.
 * Vendly LLC. Rentable micro-app. Dependency-free, no build step.
 *
 * ---------------------------------------------------------------------------
 * USE
 *   <div id="ba"></div>
 *   <script>
 *     window.BeforeAfterConfig = {
 *       target: "#ba",
 *       before: "before.jpg",
 *       after:  "after.jpg",
 *       caption: "Front yard remodel",
 *       start: 50,                 // starting split %, 0-100
 *       labels: { before: "Before", after: "After" }
 *     };
 *   </script>
 *   <script src="beforeafter.js" defer></script>
 *
 *   For several sliders, add several containers and call
 *   BeforeAfter.init({...}) once per container.
 *
 *   Best results: the two photos are the same scene, same crop/angle, similar size.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var DEFAULTS = {
    target: "#beforeafter",
    before: "",
    after: "",
    caption: "",
    start: 50,
    accent: "#ffffff",
    labels: { before: "Before", after: "After" }
  };

  function merge(user) {
    var c = {};
    for (var k in DEFAULTS) c[k] = DEFAULTS[k];
    for (var j in user) if (user.hasOwnProperty(j)) c[j] = user[j];
    c.labels = { before: DEFAULTS.labels.before, after: DEFAULTS.labels.after };
    if (user && user.labels) {
      if (user.labels.before) c.labels.before = user.labels.before;
      if (user.labels.after) c.labels.after = user.labels.after;
    }
    return c;
  }

  function escapeAttr(s) {
    return String(s == null ? "" : s).replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
    });
  }

  // Hardening (2026-07-11): only allow real image sources. A "javascript:" URL in
  // an <img src> does not execute in modern browsers, but this keeps the value
  // clean and blocks non-image schemes (data:text/html, vbscript:, etc.).
  // Allows http(s), protocol-relative, root/relative paths, and data:image/*.
  function safeSrc(raw) {
    var v = String(raw == null ? "" : raw).trim();
    if (!v) return "";
    if (/^(https?:)?\/\//i.test(v)) return v;      // http(s):// or //host
    if (/^[\/.#?]/.test(v)) return v;               // relative path
    if (/^data:image\//i.test(v)) return v;         // inline image only
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "";  // any other scheme -> drop
    return v;                                        // bare relative filename
  }

  function injectCss() {
    if (document.getElementById("beforeafter-css")) return;
    var css =
      ".ba-wrap{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;max-width:100%}" +
      ".ba{position:relative;width:100%;overflow:hidden;border-radius:12px;user-select:none;" +
      "touch-action:none;background:#eee;line-height:0;cursor:ew-resize}" +
      ".ba img{display:block;width:100%;height:auto;pointer-events:none}" +
      ".ba-after{position:relative;z-index:1}" +
      ".ba-before{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;z-index:2}" +
      ".ba-handle{position:absolute;top:0;bottom:0;width:2px;background:#fff;z-index:3;transform:translateX(-1px);" +
      "box-shadow:0 0 0 1px rgba(0,0,0,.15)}" +
      ".ba-grip{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:38px;height:38px;" +
      "border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.3);display:flex;align-items:center;" +
      "justify-content:center;z-index:4;color:#14140f;font-size:16px;font-weight:700}" +
      ".ba-tag{position:absolute;top:10px;z-index:3;background:rgba(20,20,15,.62);color:#fff;font-size:12px;" +
      "font-weight:600;padding:3px 8px;border-radius:6px;line-height:1.2}" +
      ".ba-tag.ba-b{left:10px}.ba-tag.ba-a{right:10px}" +
      ".ba-cap{font-size:14px;color:#5c5c55;margin-top:8px;line-height:1.4}";
    var el = document.createElement("style");
    el.id = "beforeafter-css";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }

  function init(userCfg) {
    var cfg = merge(userCfg || {});
    injectCss();
    var container = document.querySelector(cfg.target);
    if (!container) {
      if (window.console) console.warn("[BeforeAfter] target not found:", cfg.target);
      return;
    }
    if (!cfg.before || !cfg.after) {
      if (window.console) console.warn("[BeforeAfter] need both before and after image URLs");
      return;
    }

    var wrap = document.createElement("div");
    wrap.className = "ba-wrap";
    wrap.innerHTML =
      '<div class="ba">' +
        '<img class="ba-after" alt="' + escapeAttr(cfg.labels.after) + '" src="' + escapeAttr(safeSrc(cfg.after)) + '">' +
        '<img class="ba-before" alt="' + escapeAttr(cfg.labels.before) + '" src="' + escapeAttr(safeSrc(cfg.before)) + '">' +
        '<span class="ba-tag ba-b">' + escapeHtml(cfg.labels.before) + '</span>' +
        '<span class="ba-tag ba-a">' + escapeHtml(cfg.labels.after) + '</span>' +
        '<div class="ba-handle"><div class="ba-grip">&#8596;</div></div>' +
      '</div>' +
      (cfg.caption ? '<div class="ba-cap">' + escapeHtml(cfg.caption) + '</div>' : '');

    container.innerHTML = "";
    container.appendChild(wrap);

    var ba = wrap.querySelector(".ba");
    var before = wrap.querySelector(".ba-before");
    var handle = wrap.querySelector(".ba-handle");

    function setSplit(pct) {
      pct = clamp(pct, 0, 100);
      // Reveal the "before" image from the left up to pct; clip the rest on the right.
      before.style.clipPath = "inset(0 " + (100 - pct) + "% 0 0)";
      before.style.webkitClipPath = "inset(0 " + (100 - pct) + "% 0 0)";
      handle.style.left = pct + "%";
    }
    setSplit(clamp(cfg.start, 0, 100));

    function pctFromEvent(clientX) {
      var rect = ba.getBoundingClientRect();
      if (rect.width === 0) return cfg.start;
      return ((clientX - rect.left) / rect.width) * 100;
    }

    var dragging = false;
    function down(e) {
      dragging = true;
      var x = e.touches ? e.touches[0].clientX : e.clientX;
      setSplit(pctFromEvent(x));
      e.preventDefault();
    }
    function move(e) {
      if (!dragging) return;
      var x = e.touches ? e.touches[0].clientX : e.clientX;
      setSplit(pctFromEvent(x));
    }
    function up() { dragging = false; }

    ba.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    ba.addEventListener("touchstart", down, { passive: false });
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);

    // Keyboard accessibility: focus the slider, arrow keys nudge it.
    ba.setAttribute("tabindex", "0");
    ba.setAttribute("role", "slider");
    ba.setAttribute("aria-label", cfg.caption || "Before and after image comparison");
    var current = clamp(cfg.start, 0, 100);
    ba.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") { current = clamp(current - 4, 0, 100); setSplit(current); e.preventDefault(); }
      if (e.key === "ArrowRight") { current = clamp(current + 4, 0, 100); setSplit(current); e.preventDefault(); }
    });
  }

  window.BeforeAfter = { init: init };
  function autostart() { if (window.BeforeAfterConfig) init(window.BeforeAfterConfig); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autostart);
  } else {
    autostart();
  }
})();
